import { SEARCH_ISSUES_PERIOD_VALUES } from "./constants-CEZaLsGe.js";
import { isNumericId, validateSlugOrId } from "./slug-validation-MJ3jU0Hd.js";
import { ParamOrganizationSlug, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { hasAgentProvider } from "./provider-factory-Cs_2DBhe.js";
import { withProviderFallback } from "./provider-fallback-L61Pbfxx.js";
import { searchIssuesAgent } from "./agent-BIAUd2t1.js";
import { formatExplanation, formatIssueResults } from "./formatters-3Z1cdvzp.js";
import { z } from "zod";
import { getActiveSpan, setTag } from "@sentry/core";

//#region src/tools/catalog/search-issues.ts
const ProjectSlugOrIdSchema = z.string().trim().superRefine(validateSlugOrId);
function buildIssueSearchRepairPrompt(params) {
	return [
		"Fix this Sentry issue search request.",
		"The query may be natural language, Sentry issue search syntax, or a mix of both.",
		"Explicit Sentry search terms are authoritative. A token in field:value syntax must be preserved exactly unless Sentry would reject that specific token.",
		"Magic values in explicit syntax, such as me and latest, are valid and must be preserved.",
		"Do not remove, replace, broaden, simplify, or canonicalize valid explicit filters.",
		"Only translate natural-language words around explicit filters, add missing filters that are clearly requested, or correct syntax that would fail.",
		"Sort may be corrected when the requested sort conflicts with the user's intent, but sort must stay out of the query string.",
		"",
		`User query: ${params.query}`,
		"Current parameters:",
		JSON.stringify({ sort: params.sort }, null, 2)
	].join("\n");
}
var search_issues_default = defineTool({
	name: "search_issues",
	skills: [
		"inspect",
		"triage",
		"seer"
	],
	requiredScopes: ["event:read"],
	description: [
		"Search for grouped issues/problems in Sentry - returns a LIST of issues, NOT counts or aggregations.",
		"",
		"Provide `query` as natural language or Sentry issue search syntax. When an embedded agent is configured, it fixes query and sort before running while preserving explicit Sentry search syntax.",
		"",
		"Returns grouped issues with metadata like title, status, and user count.",
		"",
		"Common Query Syntax:",
		"- is:unresolved / is:resolved / is:ignored / is:for_review / is:new / is:regressed / is:escalating",
		"- level:error / level:warning",
		"- firstSeen:-24h / lastSeen:-7d",
		"- assigned:me / assigned_or_suggested:me",
		"- release:latest",
		"- issue.category:feedback",
		"- issue.priority:high",
		"- environment:production",
		"- userCount:>100",
		"",
		"DO NOT USE FOR COUNTS/AGGREGATIONS → use search_events",
		"DO NOT USE FOR individual events with timestamps → use search_events",
		"DO NOT USE FOR details about a specific issue → use get_sentry_resource",
		"",
		"<examples>",
		"search_issues(organizationSlug='my-org', query='critical bugs from last week')",
		"search_issues(organizationSlug='my-org', query='is:unresolved is:unassigned', sort='freq')",
		"search_issues(organizationSlug='my-org', query='level:error firstSeen:-24h', projectSlugOrId='my-project')",
		"</examples>",
		"",
		"<hints>",
		"- If the user passes a parameter in the form of name/otherName, it's likely in the format of <organizationSlug>/<projectSlugOrId>.",
		"- Parse org/project notation directly without calling find_organizations or find_projects.",
		"- The projectSlugOrId parameter accepts both project slugs (e.g., 'my-project') and numeric IDs (e.g., '123456').",
		"</hints>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug,
		query: z.string().trim().default("is:unresolved").describe("Natural language or Sentry issue search query syntax."),
		sort: z.enum([
			"date",
			"freq",
			"new",
			"user",
			"recommended"
		]).default("date").describe("Sort order: date (last seen), freq (frequency), new (first seen), user (user count), recommended (Sentry's prioritized ranking)"),
		projectSlugOrId: ProjectSlugOrIdSchema.nullable().default(null).describe("The project's slug or numeric ID (optional)"),
		regionUrl: ParamRegionUrl.nullable().default(null),
		limit: z.number().min(1).max(100).default(10).describe("Maximum number of issues to return (1-100)"),
		period: z.enum(SEARCH_ISSUES_PERIOD_VALUES).default("30d").describe("Time window for issue search results. Controls which issues are returned based on when they had activity. Default 30d is a balance between coverage and query performance; use 24h for very recent issues or 90d for broader historical searches."),
		includeExplanation: z.boolean().default(false).describe("Include explanation of how the query was translated or repaired")
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		setTag("organization.slug", params.organizationSlug);
		if (params.projectSlugOrId) if (isNumericId(params.projectSlugOrId)) setTag("project.id", params.projectSlugOrId);
		else setTag("project.slug", params.projectSlugOrId);
		let query;
		let sort;
		let explanation;
		let projectId;
		if (params.projectSlugOrId) {
			const projectSlugOrId = ProjectSlugOrIdSchema.parse(params.projectSlugOrId);
			if (isNumericId(projectSlugOrId)) projectId = projectSlugOrId;
			else {
				const project = await apiService.getProject({
					organizationSlug: params.organizationSlug,
					projectSlugOrId
				});
				projectId = String(project.id);
			}
		}
		if (hasAgentProvider()) {
			const translatedQuery = await withProviderFallback({
				operation: "search_issues.rewrite",
				fallback: () => ({
					query: params.query,
					sort: params.sort,
					explanation: ""
				}),
				run: async () => (await searchIssuesAgent({
					query: buildIssueSearchRepairPrompt({
						query: params.query,
						sort: params.sort
					}),
					organizationSlug: params.organizationSlug,
					apiService,
					projectId
				})).result
			});
			query = translatedQuery.query ?? params.query;
			sort = translatedQuery.sort || params.sort;
			explanation = translatedQuery.explanation;
		} else {
			query = params.query;
			sort = params.sort;
		}
		const issues = await apiService.listIssues({
			organizationSlug: params.organizationSlug,
			projectId,
			query,
			sortBy: sort,
			limit: params.limit,
			statsPeriod: params.period
		});
		getActiveSpan()?.setAttribute("gen_ai.tool.call.result.count", issues.length);
		let output = "";
		if (params.includeExplanation && explanation) {
			output += `# Search Results for "${params.query}"\n\n`;
			output += `**Suggested presentation:** Cards work well for these issues, with status, assignee, and issue ID links visible.\n\n`;
			output += `## Query Translation\n`;
			output += `Input query: "${params.query}"\n`;
			output += `Sentry query: \`${query}\``;
			output += `\nSort: ${sort}`;
			output += `\n\n`;
			if (explanation) {
				output += formatExplanation(explanation);
				output += `\n\n`;
			}
			output += formatIssueResults({
				issues,
				organizationSlug: params.organizationSlug,
				projectSlugOrId: params.projectSlugOrId ?? void 0,
				query,
				regionUrl: params.regionUrl ?? void 0,
				host: context.sentryHost,
				protocol: context.sentryProtocol,
				inputQuery: params.query,
				skipHeader: true,
				experimentalMode: context.experimentalMode ?? false,
				availableToolNames: context.availableToolNames,
				directToolNames: context.directToolNames
			});
		} else output = formatIssueResults({
			issues,
			organizationSlug: params.organizationSlug,
			projectSlugOrId: params.projectSlugOrId ?? void 0,
			query,
			regionUrl: params.regionUrl ?? void 0,
			host: context.sentryHost,
			protocol: context.sentryProtocol,
			inputQuery: params.query,
			skipHeader: false,
			experimentalMode: context.experimentalMode ?? false,
			availableToolNames: context.availableToolNames,
			directToolNames: context.directToolNames
		});
		return output;
	}
});

//#endregion
export { search_issues_default };
//# sourceMappingURL=search-issues-CQ0efOru.js.map